"use client";

import { useState, useEffect } from "react";
import { Bell, Check, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useSession } from "next-auth/react";

interface BackInStockFormProps {
  productId: string;
}

export default function BackInStockForm({ productId }: BackInStockFormProps) {
  const t = useTranslations("product");
  const { data: session } = useSession();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);

  // Pre-fill email if logged in
  useEffect(() => {
    if (session?.user?.email) {
      setEmail(session.user.email);
    }
  }, [session]);

  // Check if already subscribed
  useEffect(() => {
    if (session?.user?.email) {
      fetch(`/api/back-in-stock?productId=${productId}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.subscribed) {
            setAlreadySubscribed(true);
          }
        })
        .catch(() => {});
    }
  }, [session, productId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || loading) return;

    setLoading(true);
    try {
      const res = await fetch("/api/back-in-stock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), productId }),
      });

      if (res.ok) {
        setSuccess(true);
      }
    } catch {
      // silent fail
    } finally {
      setLoading(false);
    }
  };

  if (alreadySubscribed || success) {
    return (
      <div className="mt-4 flex items-center gap-2 rounded-lg border border-brand-gold/30 bg-brand-gold/5 px-4 py-3">
        <Check className="h-4 w-4 text-brand-gold shrink-0" />
        <p className="text-sm text-brand-dark">
          {success ? t("notifySuccess") : t("notifySubscribed")}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4">
      <div className="rounded-lg border border-brand-dark/10 bg-brand-cream/50 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Bell className="h-4 w-4 text-brand-gold" />
          <p className="text-sm font-medium text-brand-dark">
            {t("notifyMe")}
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("notifyEmail")}
            required
            className="flex-1 rounded-lg border border-brand-dark/10 bg-white px-3 py-2 text-sm text-brand-dark placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all"
          />
          <button
            type="submit"
            disabled={loading || !email.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-dark px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-gold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Bell className="h-3.5 w-3.5" />
            )}
            {t("notifyMe")}
          </button>
        </div>
      </div>
    </form>
  );
}
